/* eslint-disable react/no-unused-prop-types */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { compose } from 'redux';
import cn from 'classnames';
import {
  linkType,
  responsiveImageType,
  responsiveVideoType,
} from '../../propTypes';
import SlideDefaultProps from '../../defaultProps/Slide';
import Caption from './SlideCaption';
import ImageOrVideo from '../ImageOrVideo';
import OverlayImage from './OverlayImage';
import {
  getCarouselSlide,
  getCarouselSlideCount,
  getCarouselSlideIndex,
  getCarouselVideoCurrentTime,
  hasActiveCountdown,
  isCarouselPaused,
  getSliceContentType,
} from '../../selectors';
import {
  carouselNextSlideCreator,
  carouselVideoPlayingCreator,
  carouselVideoProgressCreator,
  trackImpressionEventCreator,
} from '../../actions';
import { CTA_TYPE_BRAZE } from '../../models/ContentCard';
import theme, { makeResponsiveSettings, cardHover } from '../../styles/theme';
import Link from '../Link';
import get from '../../helpers/get';

class Slide extends PureComponent {
  componentDidUpdate() {
    const { isCurrent, ctaHeader = {}, onSliceInView } = this.props;
    const type = get(ctaHeader, ['type']);

    if (!isCurrent || type !== CTA_TYPE_BRAZE) {
      return;
    }

    onSliceInView(ctaHeader);
  }

  onVideoEnded = () => {
    const { nextSlide, isVideoLooping } = this.props;
    if (!isVideoLooping) {
      nextSlide();
    }
  };

  getAudioClassNames = () => {
    const {
      classes, audioButtonPosition, contentType,
    } = this.props;
    return cn(
      classes.audioButton,
      {
        show: audioButtonPosition !== 'Hidden',
        topLeft: audioButtonPosition === 'Top left',
        bottomLeft: !audioButtonPosition || audioButtonPosition === 'Bottom left',
        header1: contentType === 'header_1',
      },
    );
  };

  renderCaption() {
    const {
      carouselId, contentTopBiases, slideIndex, overlayImage,
      replaceTitleWithVectorImage,
    } = this.props;
    return (
      <Caption
        key="caption"
        carouselId={carouselId}
        topBiases={contentTopBiases}
        slideIndex={slideIndex}
        headerImage={replaceTitleWithVectorImage ? overlayImage : undefined}
      />
    );
  }

  renderChildren() {
    const {
      classes, image, isCurrent, isVideoLooping, onVideoPlaying, onVideoProgress,
      overlayImage, video, videoCurrentTime, replaceTitleWithVectorImage, carouselId,
    } = this.props;

    return [
      <ImageOrVideo
        key="image"
        carouselId={carouselId}
        className={classes.image}
        imageClassName={classes.image}
        videoClassName={this.getAudioClassNames()}
        image={image}
        loopVideo={isVideoLooping}
        onVideoEnded={this.onVideoEnded}
        onVideoPlaying={onVideoPlaying}
        onVideoProgress={onVideoProgress}
        video={video}
        videoPaused={!isCurrent}
        videoCurrentTime={videoCurrentTime}
      />,
      !replaceTitleWithVectorImage && <OverlayImage key="overlay" image={overlayImage} />,
      this.renderCaption(),
    ];
  }

  render() {
    const {
      classes, className, ctaHeader,
    } = this.props;
    return (
      <div className={cn(className, classes.slide)}>
        {ctaHeader ? (
          <Link link={ctaHeader} className={cn(className, classes.link)}>
            {this.renderChildren()}
          </Link>
        ) : this.renderChildren()}
      </div>
    );
  }
}

Slide.propTypes = {
  carouselId: PropTypes.number.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  contentTopBiases: PropTypes.objectOf(PropTypes.number),
  image: responsiveImageType,
  isCurrent: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types -- used in styleSlide.js
  isDark: PropTypes.shape({
    desktop: PropTypes.bool.isRequired,
    mobile: PropTypes.bool.isRequired,
  }),
  isVideoLooping: PropTypes.bool,
  nextSlide: PropTypes.func.isRequired,
  onSliceInView: PropTypes.func.isRequired,
  onVideoPlaying: PropTypes.func.isRequired,
  onVideoProgress: PropTypes.func.isRequired,
  overlayImage: responsiveImageType,
  slideIndex: PropTypes.number.isRequired,
  video: responsiveVideoType,
  videoCurrentTime: PropTypes.number,
  ctaHeader: linkType,
  replaceTitleWithVectorImage: PropTypes.bool,
  audioButtonPosition: PropTypes.string,
  contentType: PropTypes.string,
};

Slide.defaultProps = SlideDefaultProps;

const mapStateToProps = (state, { carouselId, slideIndex }) => {
  const slide = getCarouselSlide(state, carouselId, slideIndex);
  return {
    ...slide,
    hasActiveCountdown: hasActiveCountdown(state, carouselId, slideIndex),
    isCurrent: getCarouselSlideIndex(state, carouselId) === slideIndex,
    isVideoLooping: isCarouselPaused(state, carouselId)
    || getCarouselSlideCount(state, carouselId) < 2,
    videoCurrentTime: getCarouselVideoCurrentTime(state, carouselId),
    replaceTitleWithVectorImage: slide.vector_image_location === 'Replace Title',
    contentType: getSliceContentType(state, carouselId),
    audioButtonPosition: slide.sound_button_position,
  };
};

const mapDispatchToProps = (dispatch, { carouselId }) => ({
  onSliceInView: ctaHeader => dispatch(trackImpressionEventCreator(ctaHeader)),
  onVideoPlaying: duration => dispatch(carouselVideoPlayingCreator(carouselId, duration)),
  onVideoProgress: currentTime => dispatch(carouselVideoProgressCreator(carouselId, currentTime)),
  nextSlide: () => dispatch(carouselNextSlideCreator(carouselId)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),

  injectSheet({
    slide: {
      position: 'relative',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      backgroundColor: ({ backgroundColor }) => backgroundColor,
      ...makeResponsiveSettings(['color', 'isDark', isDark => (
        isDark ? theme.colors.black : theme.colors.white
      )]),
    },
    link: {
      extend: 'slide',
      color: 'inherit',
      ...cardHover('$image'),
      '& *': {
        color: 'inherit',
      },
      '&:hover, &:visited, &:focus': {
        color: 'inherit',
      },
    },
    image: {
      width: '100%',
      height: '100%',
    },
    audioButton: {
      display: 'none',
      left: theme.padding.horizontal,
      padding: '0 !important',
      '&.show': {
        display: 'flex',
      },
      [theme.breakpoints.up('md')]: {
        left: 40,
      },
      [theme.breakpoints.up('lg')]: {
        left: 60,
      },
      '&.topLeft': {
        top: 0,
        [theme.breakpoints.up('md')]: {
          top: theme.spacing.vertical.xs - (theme.spacing.vertical.xs / 2),
        },
        [theme.breakpoints.up('lg')]: {
          top: 40 - (theme.padding.gutter / 2),
        },
      },
      '&.bottomLeft': {
        bottom: 0,
        [theme.breakpoints.up('md')]: {
          bottom: theme.spacing.vertical.xs - (theme.spacing.vertical.xs / 2),
        },
        [theme.breakpoints.up('lg')]: {
          bottom: 40 - (theme.padding.gutter / 2),
        },
      },
      '&.header1.topLeft': {
        top: 11,
        [theme.breakpoints.up('md')]: {
          top: 24,
        },
        [theme.breakpoints.up('lg')]: {
          top: 46,
        },
      },
      '&.header1.bottomLeft': {
        bottom: 11,
        [theme.breakpoints.up('md')]: {
          bottom: 24,
        },
        [theme.breakpoints.up('lg')]: {
          bottom: 46,
        },
      },
    },
  }),
)(Slide);
